/**
 * Work helpers: dates in plain words, repeat rules in plain words, and the
 * small shared lookups (people, sources, goals, AI employees, who "me" is)
 * every Work screen needs. Lookups are cached per tab so the drawer opens
 * instantly on the second click.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { fetchUsers } from "@/lib/use-users";
import type { Area, Goal, Person, Recurrence, SourceLite, TeamData, WorkTask } from "./types";

// ─── Words ───────────────────────────────────────────────────────────────────

export const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const DAY_LETTER = ["S", "M", "T", "W", "T", "F", "S"] as const;
/** Monday first, the way people plan a week. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export const AREAS: Array<{ value: Area; label: string; color: string }> = [
  { value: "growth", label: "Growth", color: "pink" },
  { value: "customers", label: "Customers", color: "sky" },
  { value: "product", label: "Product", color: "violet" },
  { value: "support", label: "Support", color: "amber" },
  { value: "ops", label: "Running the business", color: "slate" },
  { value: "money", label: "Money", color: "emerald" },
];
export const areaLabel = (a: string | null | undefined) => AREAS.find((x) => x.value === a)?.label ?? "Other";
export const areaColor = (a: string | null | undefined) => AREAS.find((x) => x.value === a)?.color ?? "zinc";

/** "every day", "every Mon, Wed, Fri", "3 times a week", "every 2 months". */
export function repeatLabel(r: Recurrence | null | undefined): string {
  if (!r) return "";
  const n = r.interval && r.interval > 1 ? r.interval : 1;
  if (r.freq === "daily") return n === 1 ? "every day" : `every ${n} days`;
  if (r.freq === "monthly") return n === 1 ? "every month" : `every ${n} months`;
  const days = [...(r.days ?? [])].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
  if (days.length === 7) return "every day";
  if (days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))) return "every weekday";
  if (days.length) return `every ${days.map((d) => DAY_SHORT[d]).join(", ")}`;
  if (r.times && r.times > 1) return `${r.times} times a week`;
  return n === 1 ? "every week" : `every ${n} weeks`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

// ─── Dates ───────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** The server sends task times as a JS date string; parse anything. */
export function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  let d = new Date(s);
  if (Number.isNaN(d.getTime())) d = new Date(String(s).replace(/\s*\(.*\)\s*$/, ""));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local YYYY-MM-DD. */
export function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export const todayYmd = () => ymd(new Date());

/** A YYYY-MM-DD as a local Date at noon (safe from timezone edges). */
export function fromYmd(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
}

export function addDays(day: string, n: number): string {
  const d = fromYmd(day);
  d.setDate(d.getDate() + n);
  return ymd(d);
}

export function addMonths(day: string, n: number): string {
  const d = fromYmd(day);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return ymd(d);
}

/** Monday of the week a day is in. */
export function weekStart(day: string): string {
  const wd = fromYmd(day).getDay();
  return addDays(day, -((wd + 6) % 7));
}

export const weekday = (day: string) => fromYmd(day).getDay();

/** "Tue, Oct 27" / "Tuesday" etc. */
export function fmtDay(day: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }): string {
  return fromYmd(day).toLocaleDateString(undefined, opts);
}

/** "Today", "Tomorrow", "Yesterday" or "Thu, Oct 29". */
export function relDay(day: string, today = todayYmd()): string {
  if (day === today) return "Today";
  if (day === addDays(today, 1)) return "Tomorrow";
  if (day === addDays(today, -1)) return "Yesterday";
  return fmtDay(day, { weekday: "long", month: "short", day: "numeric" });
}

/** "9:00" → "9 am", "13:30" → "1:30 pm". */
export function timeLabel(hhmm: string | null | undefined): string {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const hh = h ?? 0;
  const suffix = hh >= 12 ? "pm" : "am";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return m ? `${h12}:${pad(m)} ${suffix}` : `${h12} ${suffix}`;
}

export function localHHmm(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Wall-clock "HH:mm" of an instant in a timezone. */
export function hhmmIn(d: Date, tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  } catch {
    return localHHmm(d);
  }
}

/** The instant of a wall-clock time on a day in a timezone (as the server does it). */
export function zonedInstant(day: string, time: string, tz: string): Date {
  const guess = new Date(`${day}T${time}:00Z`);
  try {
    const local = new Date(guess.toLocaleString("en-US", { timeZone: tz }));
    const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
    return new Date(guess.getTime() + (utc.getTime() - local.getTime()));
  } catch {
    return guess;
  }
}

/**
 * A task "has no time" when it sits at noon in the workspace's timezone (the
 * quick add saves dateless-time tasks there) or at noon on this computer.
 */
export function isTimeless(d: Date, wsTz: string): boolean {
  return hhmmIn(d, wsTz) === "12:00" || localHHmm(d) === "12:00";
}

/** Split a task's due into the drawer's date + optional time. */
export function splitDue(due: string | null, wsTz: string): { date: string; time: string } {
  const d = parseDate(due);
  if (!d) return { date: "", time: "" };
  return { date: ymd(d), time: isTimeless(d, wsTz) ? "" : localHHmm(d) };
}

/** Date (+ optional time) back to an instant. No time = noon in the workspace. */
export function joinDue(date: string, time: string, wsTz: string): string | null {
  if (!date) return null;
  if (time) return new Date(`${date}T${time}:00`).toISOString();
  return zonedInstant(date, "12:00", wsTz).toISOString();
}

/** The local day a task is due, or null. */
export function dueDay(t: Pick<WorkTask, "due_at">): string | null {
  const d = parseDate(t.due_at);
  return d ? ymd(d) : null;
}

/** Shown time for a task ("3 pm"), or "" when it has no time. */
export function dueTime(t: Pick<WorkTask, "due_at">, wsTz: string): string {
  const d = parseDate(t.due_at);
  if (!d || isTimeless(d, wsTz)) return "";
  return timeLabel(localHHmm(d));
}

/**
 * The days a routine will come back on after its current due day, inside a
 * window (mirrors the server's "next one" rule). Drawn faintly on the
 * calendar and in Upcoming so a routine reads as a rhythm, not a single row.
 */
export function projectRepeats(t: Pick<WorkTask, "due_at" | "recurrence">, from: string, to: string, max = 60): string[] {
  const r = t.recurrence;
  const base = dueDay(t);
  if (!r || !base) return [];
  const out: string[] = [];
  const n = r.interval && r.interval > 0 ? r.interval : 1;
  let cur = base;
  for (let i = 0; i < 400 && out.length < max; i++) {
    let next: string;
    if (r.freq === "weekly" && r.days && r.days.length) {
      next = addDays(cur, 1);
      let guard = 0;
      while (!r.days.includes(weekday(next)) && guard++ < 8) next = addDays(next, 1);
    } else if (r.freq === "daily") next = addDays(cur, n);
    else if (r.freq === "weekly") next = addDays(cur, r.times && r.times > 1 ? Math.max(1, Math.round(7 / r.times)) : 7 * n);
    else if (r.freq === "monthly") {
      const d = fromYmd(cur);
      d.setMonth(d.getMonth() + n);
      next = ymd(d);
    } else break;
    if (next > to) break;
    if (r.until && next > r.until) break;
    if (next >= from) out.push(next);
    cur = next;
  }
  return out;
}

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** A friendly name for an AI employee slug ("default" = not picked yet). */
export function agentName(slug: string | null | undefined, team: TeamData | null): string {
  if (!slug) return "";
  const a = team?.agents.find((x) => x.slug === slug);
  if (a) return a.name;
  if (slug === "default") return "AI employee";
  return slug.replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** "every 24 hours, next run Tue 9:00". */
export function scheduleLabel(s: { everySeconds: number; enabled: boolean; nextRunAt: string | null }): string {
  const sec = s.everySeconds;
  let every: string;
  if (sec % 604800 === 0) every = sec === 604800 ? "every week" : `every ${sec / 604800} weeks`;
  else if (sec % 86400 === 0) every = sec === 86400 ? "every day" : `every ${sec / 86400} days`;
  else if (sec % 3600 === 0) every = sec === 3600 ? "every hour" : `every ${sec / 3600} hours`;
  else if (sec % 60 === 0) every = `every ${sec / 60} minutes`;
  else every = `every ${sec} seconds`;
  if (!s.enabled) return `${every}, paused`;
  const next = parseDate(s.nextRunAt);
  if (!next) return every;
  const day = ymd(next) === todayYmd() ? "today" : ymd(next) === addDays(todayYmd(), 1) ? "tomorrow" : next.toLocaleDateString(undefined, { weekday: "short" });
  return `${every}, next run ${day} ${timeLabel(localHHmm(next))}`;
}

// ─── Lookups (cached per tab) ────────────────────────────────────────────────

export interface Me { id: string; name: string; email?: string }

let meCache: Me | null | undefined;
let meInFlight: Promise<Me | null> | null = null;

/** Who is signed in: the sign-in session first, the API as a fallback. */
export async function loadMe(): Promise<Me | null> {
  if (meCache !== undefined) return meCache;
  if (meInFlight) return meInFlight;
  meInFlight = (async () => {
    try {
      const r = await authClient.getSession();
      const u = (r as { data?: { user?: { id?: string; name?: string; email?: string } } | null })?.data?.user;
      if (u?.id) return { id: u.id, name: u.name ?? "", email: u.email };
    } catch { /* fall through */ }
    try {
      const h = await api.get<{ user: Me | null }>("/command/me");
      return h.user ?? null;
    } catch {
      return null;
    }
  })().then((m) => { meCache = m; meInFlight = null; return m; });
  return meInFlight;
}

export function useMe(): { me: Me | null; ready: boolean } {
  const [me, setMe] = useState<Me | null>(meCache ?? null);
  const [ready, setReady] = useState(meCache !== undefined);
  useEffect(() => {
    let live = true;
    void loadMe().then((m) => { if (live) { setMe(m); setReady(true); } });
    return () => { live = false; };
  }, []);
  return { me, ready };
}

interface LookupState {
  people: Person[];
  sources: SourceLite[];
  goals: Goal[];
  team: TeamData | null;
  wsTz: string;
}

const lookupCache: LookupState = { people: [], sources: [], goals: [], team: null, wsTz: "America/Edmonton" };
let lookupAt = 0;
let lookupInFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

async function refreshLookups(force = false): Promise<void> {
  if (!force && lookupAt && Date.now() - lookupAt < 60_000) return;
  if (lookupInFlight) return lookupInFlight;
  lookupInFlight = (async () => {
    try {
    const [users, sources, goals, team, settings] = await Promise.all([
      fetchUsers(force).catch(() => []),
      api.get<{ sources: SourceLite[] }>("/sources", { lite: 1 }).then((d) => d.sources).catch(() => lookupCache.sources),
      api.get<{ goals: Goal[] }>("/command/goals").then((d) => d.goals).catch(() => lookupCache.goals),
      api.get<TeamData>("/team").catch(() => lookupCache.team),
      api.get<{ settings?: { timezone?: string | null } }>("/settings").catch(() => null),
    ]);
    lookupCache.people = users.filter((u) => u.id !== "dev-user").map((u) => ({ id: u.id, name: u.name || u.email, email: u.email, image: u.image }));
    lookupCache.sources = sources;
    lookupCache.goals = goals;
    lookupCache.team = team;
    const tz = settings?.settings?.timezone;
    if (tz && tz !== "UTC") {
      try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); lookupCache.wsTz = tz; } catch { /* keep default */ }
    }
    } finally {
      // Even a failed load counts as loaded: pages render with what they have.
      lookupAt = Date.now();
    }
  })().catch(() => undefined).finally(() => {
    lookupInFlight = null;
    listeners.forEach((fn) => fn());
  });
  return lookupInFlight;
}

/** People, traffic sources, goals, AI employees and the workspace timezone. */
export function useLookups() {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    void refreshLookups();
    return () => { listeners.delete(fn); };
  }, []);
  const reload = useCallback(() => refreshLookups(true), []);
  return { ...lookupCache, loaded: lookupAt > 0, reload };
}

/** Sources as picker options: parents, then their children indented. */
export function sourceOptions(sources: SourceLite[]): Array<{ id: string; label: string; color: string | null }> {
  const top = sources.filter((s) => !s.parent_id || !sources.some((p) => p.id === s.parent_id));
  const out: Array<{ id: string; label: string; color: string | null }> = [];
  for (const p of top) {
    out.push({ id: p.id, label: p.name, color: p.color });
    for (const c of sources.filter((s) => s.parent_id === p.id)) out.push({ id: c.id, label: `   ${c.name}`, color: c.color });
  }
  return out;
}

/** One task by id, or null when it doesn't exist. */
export async function findTask(id: string): Promise<WorkTask | null> {
  try {
    const d = await api.get<{ task: WorkTask }>(`/tasks/${encodeURIComponent(id)}`);
    return d.task ?? null;
  } catch {
    return null;
  }
}

/** Only the fields the API accepts, for a PATCH or POST body. */
export function taskBody(p: Partial<WorkTask>): Record<string, unknown> {
  const keys = [
    "title", "description", "contact_id", "assignee_id", "due_at", "status", "priority", "recurrence",
    "source_id", "goal_id", "affiliate_id", "area", "agent_slug", "handoff_url", "checklist",
  ] as const;
  const out: Record<string, unknown> = {};
  for (const k of keys) if (k in p) out[k] = p[k];
  return out;
}

export const isDone = (t: Pick<WorkTask, "status">) => t.status === "completed";
